import { useAPI } from '../contexts/apiContext';
import { APIHandler } from '../api';

// Type for any function that returns a Promise
type AsyncFunction = (...args: any[]) => Promise<any>;

// Type guard to check if a property is an async function
function isAsyncFunction(value: unknown): value is AsyncFunction {
  return typeof value === 'function';
}

export const useAPIHandler = (baseUrl: string) => {
  const { wrapAPICall } = useAPI();
  const handler = APIHandler(baseUrl);

  // Create a proxy that wraps all API calls with wrapAPICall
  const proxy = new Proxy(handler, {
    get: (target, prop: keyof typeof handler) => {
      // Get the original property
      const originalProp = target[prop];

      // If it's not a function or doesn't exist, return it as is
      if (!isAsyncFunction(originalProp)) {
        return originalProp;
      }

      // Return a wrapped version of the function that preserves types
      return (...args: Parameters<typeof originalProp>) =>
        wrapAPICall(originalProp.apply(target, args));
    },
  });

  // Return the proxy with the exact type of the handler
  return proxy as typeof handler;
};
